import { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  selectIsContextualizedMetricsFeatureFlagOn,
  selectIsMetricDefinitionsFeatureFlagOn,
  selectIsMIApplyButtonFeatureFlagOn,
} from 'core/userContext';
import { PERCENTAGE } from 'ui/card/constants';
import { spacing, colors, smallShadow, body1Bold } from 'ui/theme';
import { Analytics, LineChart, Overflow } from 'ui/icons';
import { ButtonGroup, GroupButton } from 'ui/buttonGroup';
import { Spinner } from 'ui/spinner';
import { Overview } from 'ui/illustrations';
import { Menu } from 'ui/menu';
import { ALIGNMENT } from 'ui/menu/constants';
import { IconButton, BUTTON_VARIANTS } from 'ui/button';
import {
  useTimeFiltersContext,
  useCampaignAndMetricFiltersContext,
} from 'modules/messagingInsights/context';
import { useMetricsContext } from 'modules/productAnalytics/context';
import { useGetAllUrlFilters } from 'modules/messagingInsights/hooks/useGetAllUrlFilters';
import { DefinitionWrapper } from 'modules/metricDefinitions';
import {
  metricSentenceCase,
  getChartActions,
  getChartExportingOptions,
} from 'modules/productAnalytics/utils';
import { ColChartMetricData } from './types';
import { getChartOptions } from './utils';
import { TimeSeriesGraph } from './TimeSeriesGraph';
import { NoDataMessage } from '../NoDataMessage';
import { selectAppliedCrossMetrics } from '../../selectors';

type TimeSeriesChartProps = {
  chartData: ColChartMetricData;
  numCampaignsPerMedium: Record<string, number>;
};

const ChartCard = styled.div`
  margin-bottom: ${spacing.plusHalf};
  background: ${colors.backgroundPrimary};
  border-radius: ${spacing.half};
  ${smallShadow};
`;

const GraphButtonWrapper = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: ${spacing.base} ${spacing.plusHalf};
  border-bottom: ${spacing.single} solid ${colors.borderPrimary};
`;

const ChartTitle = styled.div`
  ${body1Bold}
  display: flex;
  align-items: center;
`;

const BtnLabel = styled.div`
  display: flex;
  align-items: center;
  > span {
    margin-top: ${spacing.single};
    margin-right: ${spacing.quarter};
  }
`;

const StyledButtonGroup = styled(ButtonGroup)`
  > div {
    border: unset;
  }
`;

const ChartBody = styled.div`
  padding: ${spacing.double} ${spacing.plusHalf} ${spacing.half} ${spacing.plusHalf};
`;

const LoadingContainer = styled.div`
  height: ${spacing.x24};
`;

export const TimeSeriesChart = ({ chartData, numCampaignsPerMedium }: TimeSeriesChartProps) => {
  const metricDefFFOn = useSelector(selectIsMetricDefinitionsFeatureFlagOn);
  const contextualizedMetricsFFOn = useSelector(selectIsContextualizedMetricsFeatureFlagOn);

  const isMIApplyButtonFFOn = useSelector(selectIsMIApplyButtonFeatureFlagOn);

  const { appliedCampaignFilters } = useCampaignAndMetricFiltersContext();
  const appliedCrossMetrics = useSelector(selectAppliedCrossMetrics);

  const allFilters = isMIApplyButtonFFOn
    ? {
        ...useGetAllUrlFilters(),
        campaignFilters: appliedCampaignFilters,
        selectedCrossMetrics: appliedCrossMetrics,
      }
    : useGetAllUrlFilters();
  const { timezone } = useTimeFiltersContext();
  const { analyticsMetricsValueTypes, highLevelMetricsValueTypes } = useMetricsContext();

  const { metric, data } = chartData;
  const isLoading = data.length === 0;
  const metricType = analyticsMetricsValueTypes[metric] || highLevelMetricsValueTypes[metric];
  const isMetricRateBased = metricType === PERCENTAGE; // i.e. email delivery rate, clicks / opens

  const [selectedChartType, setSelectedChartType] = useState('opt-col');
  const groupButtons = ['opt-col', 'opt-line'];
  const chartRef = useRef();
  const chartId = metric.replace(' ', '-');
  const chartActions = getChartActions(chartId, chartRef);

  return (
    <ChartCard data-test="TimeSeriesChart">
      <ChartHeader>
        <ChartTitle>
          {metricDefFFOn && !contextualizedMetricsFFOn ? (
            <DefinitionWrapper>{metricSentenceCase(metric)}</DefinitionWrapper>
          ) : (
            metricSentenceCase(metric)
          )}
        </ChartTitle>
        <GraphButtonWrapper>
          <StyledButtonGroup id={'chart-type-toggle'} defaultSelectedId={'opt-col'}>
            {groupButtons.map((btn) => (
              // @ts-ignore
              <GroupButton id={btn} key={btn} onSelect={() => setSelectedChartType(btn)}>
                {btn === 'opt-col' ? (
                  <BtnLabel>
                    <span>Bar</span>
                    <Analytics />
                  </BtnLabel>
                ) : (
                  <BtnLabel>
                    <span>Line</span>
                    <LineChart />
                  </BtnLabel>
                )}
              </GroupButton>
            ))}
          </StyledButtonGroup>
          <Menu
            options={getChartExportingOptions(chartId)}
            handleSelectionChange={(key: string) => chartActions[key]()}
            horizontalAlignment={ALIGNMENT.RIGHT}
          >
            <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
              <Overflow />
            </IconButton>
          </Menu>
        </GraphButtonWrapper>
      </ChartHeader>
      {!isLoading && (
        <ChartBody>
          {selectedChartType === 'opt-col' && (
            <TimeSeriesGraph
              // @ts-ignore
              data={data}
              options={getChartOptions(chartData, numCampaignsPerMedium, allFilters, metricType)}
              timezone={timezone}
              type="column"
              ref={chartRef}
            />
          )}
          {selectedChartType === 'opt-line' &&
            (isMetricRateBased ? (
              <NoDataMessage
                Icon={Overview}
                title={'Percentage-based metrics cannot be displayed on line charts'}
                subtitle={'Switch to bar to view the chart for this metric.'}
                noSpacing
              />
            ) : (
              // @ts-ignore
              <TimeSeriesGraph
                // @ts-ignore
                data={data}
                options={getChartOptions(chartData, numCampaignsPerMedium, allFilters, metricType)}
                timezone={timezone}
                type="line"
                numCampaignsPerMedium={numCampaignsPerMedium}
                ref={chartRef}
              />
            ))}
        </ChartBody>
      )}
      {isLoading && (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      )}
    </ChartCard>
  );
};
